"use client";

import { useEffect, useRef } from "react";

import { buildDotField, type DotField } from "./us-dot-field";

interface DotFieldCanvasProps {
  width: number;
  height: number;
  scale: number;
  /** Dot ink, as an `r, g, b` triple. */
  ink: string;
  /** Ink alpha. The field is deliberately faint: it is a ground, not a subject. */
  alpha: number;
  onField: (field: DotField) => void;
}

/**
 * The static half of the panel. Every dot is painted once; only the marks above
 * it animate, so there is no draw loop to run.
 */
export function DotFieldCanvas({
  width,
  height,
  scale,
  ink,
  alpha,
  onField,
}: DotFieldCanvasProps) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const notify = useRef(onField);
  notify.current = onField;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || width <= 0 || height <= 0) {
      return;
    }
    const field = buildDotField(width, height, scale);
    notify.current(field);

    // A canvas takes its backing store from its width/height attributes, so
    // they have to be set in device pixels; the CSS box is sized separately.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = `rgba(${ink}, ${alpha})`;
    const r = field.radius;
    for (let i = 0; i < field.dots.length; i += 2) {
      ctx.beginPath();
      ctx.arc(
        field.dots[i] as number,
        field.dots[i + 1] as number,
        r,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }, [width, height, scale, ink, alpha]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      // `inset-0` alone leaves CSS width/height at auto, so the intrinsic size
      // from the attributes wins and the panel shows a magnified crop.
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
